export {};

interface CPProduct {
  handle: string;
  title: string;
  featuredImage?: { url: string; altText: string | null } | null;
  priceRange: {
    minVariantPrice: { amount: string; currencyCode: string };
  };
  availableForSale: boolean;
}

interface CPGraphQLResponse {
  data?: {
    collection?: {
      products?: {
        edges: { node: CPProduct }[];
        pageInfo: { hasNextPage: boolean; endCursor: string };
      };
    };
  };
}

const customCards = document.querySelectorAll<HTMLElement>('.header-subcontent');
const overviewSection = document.querySelectorAll<HTMLElement>('main .shopify-section:not(.section-custom-collection-header)');
const stepOne = document.querySelector<HTMLElement>('[data-step="1"]');
const stepTwo = document.querySelector<HTMLElement>('[data-step="2"]');
let currentOpenCardIMG: string | undefined;

const delay = (ms: number): Promise<void> => new Promise((res) => setTimeout(res, ms));

const hideOverviewSection = (): void => {
  overviewSection.forEach((section) => section.classList.add('hidden-section'));
};

const showOverviewSection = (): void => {
  overviewSection.forEach((section) => section.classList.remove('hidden-section'));
};

function smoothScroll(selector: string): void {
  document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth' });
}

customCards.forEach((card) => {
  const button = card.querySelector<HTMLButtonElement>('button');
  if (!button) return;

  button.addEventListener('click', async (e) => {
    e.preventDefault();
    currentOpenCardIMG = button.dataset.photo;

    card.classList.add('--erased');
    await delay(300);
    card.classList.add('--open');
    hideOverviewSection();
    smoothScroll('.section-custom-collection-header');
    await delay(300);

    stepOne?.classList.add('hidden-section');
    stepTwo?.classList.add('--activated');

    stepTwo?.querySelectorAll<HTMLElement>('.button--outline, .button-mobile').forEach((btn) => {
      btn.addEventListener('click', (ev) => {
        ev.preventDefault();
        card.classList.remove('--open', '--erased');
        showOverviewSection();
        stepOne?.classList.remove('hidden-section');
        stepTwo?.classList.remove('--activated');
      });
    });
  });
});

// Shop-specific setup
const shopUrl = 'https://kaseme-dev-page.myshopify.com';
const accessToken = 'a815422836370e8d05bd0c6a813dbaa5';

function buildImagePicture(fileUrl: string, alt: string, width: number, height: number): string {
  return `<picture>
    <source media="(min-width:750px)" srcset="${fileUrl}600 1x, ${fileUrl}1200 2x">
    <source media="(max-width:749px)" srcset="${fileUrl}300 1x, ${fileUrl}600 2x">
    <img src="${fileUrl}600" alt="${alt}" width="${width}" height="${height}" />
  </picture>`;
}

const fetchCollectionProductsByProductType = (prodType: string): void => {
  const country = (document.querySelector<HTMLElement>('body[data-country]')?.dataset.country ?? '').toUpperCase() === 'CA' ? 'CA' : 'US';
  const lang = document.documentElement.lang.toUpperCase();

  const collectionProductsQuery = `query FilteredProducts @inContext(language: ${lang}, country: ${country}) {
    collection(handle: "test-custom") {
      products(first: 200, filters: [{ productType: "${prodType}"}]) {
        edges {
          node {
            handle
            title
            featuredImage { url altText id }
            priceRange { minVariantPrice { amount currencyCode } }
            availableForSale
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  }`;

  fetch(`${shopUrl}/api/2026-04/graphql.json`, {
    method: 'post',
    headers: {
      'Content-Type': 'application/graphql',
      'X-Shopify-Storefront-Access-Token': accessToken,
    },
    body: collectionProductsQuery,
  })
    .then((res) => res.json())
    .then((response: CPGraphQLResponse) => {
      const edges = response.data?.collection?.products?.edges;
      if (!edges || edges.length === 0) {
        const article = document.querySelector<HTMLElement>('[data-step="2"] article');
        if (article) {
          article.innerHTML = lang === 'FR'
            ? '<p>Une erreur est survenue. Veuillez réassayer</p>'
            : '<p>An error has occurred. Please try again</p>';
        }
        return;
      }

      const phoneInfoToggle = document.querySelector<HTMLElement>('[data-toggle-phone-info-dialog]');
      if (phoneInfoToggle) {
        const isNonPhone = prodType.includes('iPad') || prodType.includes('ChargePods') || prodType.includes('Kindle Case - Custom');
        isNonPhone ? phoneInfoToggle.setAttribute('hidden', 'true') : phoneInfoToggle.removeAttribute('hidden');
      }

      const iPhoneProducts: CPProduct[] = [];
      const googleProducts: CPProduct[] = [];
      const samsungProducts: CPProduct[] = [];
      const lgProducts: CPProduct[] = [];
      const otherProducts: CPProduct[] = [];

      edges.forEach(({ node }) => {
        const t = node.title;
        if (t.includes('iPhone')) iPhoneProducts.push(node);
        else if (t.includes('Google')) googleProducts.push(node);
        else if (t.includes('Samsung') || t.includes('Galaxy')) samsungProducts.push(node);
        else if (t.includes('LG')) lgProducts.push(node);
        else otherProducts.push(node);
      });

      const langPrefix = lang === 'FR' ? '/fr' : '';
      const labelFR = 'Choisissez votre appareil';
      const labelEN = 'Choose your device';
      const label = lang === 'FR' ? labelFR : labelEN;

      const buildOption = (product: CPProduct, displayTitle: string): string =>
        `<option data-device-option="${langPrefix}/products/${product.handle}" data-device-img="${product.featuredImage?.url ?? ''}" value="${displayTitle}"${product.availableForSale ? '' : ' disabled'}>${displayTitle}</option>`;

      const buildOptGroup = (groupLabel: string, products: CPProduct[], getTitle: (p: CPProduct) => string): string => {
        if (!products.length) return '';
        return `<optgroup label="${groupLabel}">${products.map((p) => buildOption(p, getTitle(p))).join('')}</optgroup>`;
      };

      const productGroupsHtml = [
        `<option>${label}</option>`,
        buildOptGroup('iPhone', iPhoneProducts, (p) => p.title.replace('Custom - ', '').replace('Custom Folio - ', '')),
        buildOptGroup('Google', googleProducts, (p) => p.title.replace('Custom - Google ', '')),
        buildOptGroup('Samsung', samsungProducts, (p) => p.title.replace('Custom - ', '').replace('Custom Folio - ', '')),
        buildOptGroup('LG', lgProducts, (p) => p.title.replace('Custom - ', '')),
        otherProducts.map((p) => buildOption(p, p.title.replace(' - Custom Folio Case', ''))).join(''),
      ].join('');

      const variantSelector = document.querySelector<HTMLElement>('[data-selector-section]');
      if (!variantSelector) return;
      variantSelector.innerHTML = `<select class="select__select" name="options[Device]">${productGroupsHtml}</select>`;

      const selector = variantSelector.querySelector<HTMLSelectElement>('select')!;
      const nexStepButton = document.querySelector<HTMLButtonElement>('[data-next-step]')!;
      const titleEl = document.querySelector<HTMLElement>('.variant-selector h2')!;
      const priceEl = document.querySelector<HTMLElement>('.variant-selector .price')!;

      nexStepButton.disabled = true;

      const typeMap: Record<string, { className: string; titleKey: string; priceKey: string }> = {
        'Custom Phone Case':          { className: 'button button--tertiary',  titleKey: 'impact',      priceKey: 'impact' },
        'Custom Phone Case - Folio':  { className: 'button button--secondary', titleKey: 'folio',       priceKey: 'folio' },
        'iPad Folio Case - Custom':   { className: 'button button--primary',   titleKey: 'ipad',        priceKey: 'ipad' },
        'Custom ChargePods':          { className: 'button button--fourth',    titleKey: 'chargepods',  priceKey: 'chargepods' },
        'Kindle Case - Custom':       { className: 'button button--sixth',     titleKey: 'kindle',      priceKey: 'kindle' },
      };
      const mapping = typeMap[prodType];
      if (mapping) {
        nexStepButton.className = mapping.className;
        titleEl.innerHTML = (titleEl.dataset as Record<string, string>)[mapping.titleKey] ?? '';
        priceEl.innerHTML = (priceEl.dataset as Record<string, string>)[mapping.priceKey] ?? '';
      }

      // Set initial image from the selected card photo
      const imageSection = document.querySelector<HTMLElement>('.image-section')!;
      const existingImg = imageSection.querySelector<HTMLImageElement>('img');
      const fileUrl = currentOpenCardIMG ?? '';
      imageSection.innerHTML = buildImagePicture(fileUrl, selector.value, existingImg?.offsetWidth ?? 0, existingImg?.offsetHeight ?? 0);

      selector.addEventListener('change', () => {
        const isPlaceholder = selector.value === labelEN || selector.value === labelFR;
        if (!isPlaceholder) {
          nexStepButton.disabled = false;
          const selectedOption = selector.selectedOptions[0] as HTMLOptionElement & { dataset: DOMStringMap };
          nexStepButton.dataset.nextStep = selectedOption.dataset.deviceOption ?? '';

          const deviceImg = selectedOption.dataset.deviceImg ?? '';
          const sectionImg = imageSection.querySelector<HTMLImageElement>('img');
          imageSection.innerHTML = buildImagePicture(deviceImg, selector.value, sectionImg?.offsetWidth ?? 0, sectionImg?.offsetHeight ?? 0);

          const chargepodsRef = document.querySelector<HTMLElement>('[data-chargepods-shell]');
          if (chargepodsRef) {
            const productPriceEl = document.querySelector<HTMLElement>('.product-section .price');
            if (productPriceEl) {
              if (selector.value === 'Custom - ChargePod - Shell only') {
                productPriceEl.innerHTML = chargepodsRef.dataset.chargepodsShell ?? '';
              } else if (selector.value === 'Custom - ChargePod - Battery + Shell') {
                productPriceEl.innerHTML = chargepodsRef.dataset.chargepods ?? '';
              }
            }
          }
        } else {
          nexStepButton.disabled = true;
          nexStepButton.dataset.nextStep = '';
        }
      });
    })
    .catch((e) => console.error(e));
};

// Start buttons
document.querySelectorAll<HTMLElement>('[data-start-custom]').forEach((button) => {
  button.addEventListener('click', (e) => {
    e.preventDefault();
    const prodType = button.dataset.startCustom;
    if (prodType) fetchCollectionProductsByProductType(prodType);
  });
});

// Next step button
const nextStepButton = document.querySelector<HTMLButtonElement>('[data-next-step]');
nextStepButton?.addEventListener('click', (e) => {
  e.preventDefault();
  const href = nextStepButton.dataset.nextStep;
  if (href) window.location.href = href;
});

// Current device dropdown toggle
const currentDevice = document.querySelector<HTMLElement>('[data-current-device]');
if (currentDevice) {
  currentDevice.addEventListener('click', (e) => {
    e.preventDefault();
    const dataSelector = document.querySelector<HTMLElement>('[data-selector]');
    if (!dataSelector) return;

    const clickOutside = (ev: Event): void => {
      if (!ev.composedPath().includes(dataSelector)) {
        if (!(ev.target as HTMLElement)?.dataset.currentDevice) {
          dataSelector.classList.remove('--visible');
          document.removeEventListener('click', clickOutside, true);
        }
      }
    };

    if (dataSelector.classList.contains('--visible')) {
      dataSelector.classList.remove('--visible');
      document.removeEventListener('click', clickOutside, true);
    } else {
      dataSelector.classList.add('--visible');
      document.addEventListener('click', clickOutside, true);
    }
  });
}

// Phone info popup
const infoPopupButton = document.querySelector<HTMLElement>('[data-toggle-phone-info-dialog]');
const infoPopup = document.querySelector<HTMLDialogElement>('[data-phone-info]');
if (infoPopupButton && infoPopup) {
  infoPopupButton.addEventListener('click', (e) => {
    e.preventDefault();
    infoPopup.showModal();
    document.body.classList.add('--no-scroll');

    infoPopup.querySelector<HTMLElement>('[data-dialog-toggle]')?.addEventListener('click', (ev) => {
      ev.preventDefault();
      infoPopup.close();
      document.body.classList.remove('--no-scroll');
    });

    infoPopup.querySelectorAll<HTMLButtonElement>('.dialog-nav button').forEach((button) => {
      button.addEventListener('click', (ev) => {
        ev.preventDefault();
        infoPopup.querySelector<HTMLElement>('.dialog-nav button.active')?.classList.remove('active');
        button.classList.add('active');
        infoPopup.querySelector<HTMLElement>('[data-dialog-group].active')?.classList.remove('active');
        const target = button.dataset.target;
        if (target) infoPopup.querySelector<HTMLElement>(`[data-dialog-group="${target}"]`)?.classList.add('active');
      });
    });
  });
}

// Auto-click category from URL param
const urlParams = new URLSearchParams(window.location.search);
const productCategory = urlParams.get('category');
const categoryMap: Record<string, string> = {
  'custom-phone-case':         'Custom Phone Case',
  'custom-phone-case-folio':   'Custom Phone Case - Folio',
  'ipad-folio-case-custom':    'iPad Folio Case - Custom',
  'custom-chargepods':         'Custom ChargePods',
};
if (productCategory && categoryMap[productCategory]) {
  document.querySelector<HTMLElement>(`[data-start-custom="${categoryMap[productCategory]}"]`)?.click();
}
